import {
  Box,
  Button,
  Flex,
  Heading,
  LinkBox,
  LinkOverlay,
  Progress,
  SimpleGrid,
  Spinner,
  Stack,
  Table,
  Tbody,
  Text,
  Th,
  Thead,
  Tr,
  useBreakpointValue,
} from "@chakra-ui/react";
import type { TFunction } from "i18next";
import { useEffect, useRef, useState, type RefObject } from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import {
  CallOffFollowUpReasonDto,
  useGetFrameworkOverviewQuery,
  type CallOffFollowUpDto,
  type FrameworkUsageDto,
} from "../../autogen/bff-api";
import { SelectableCard } from "../../common/input/SelectableCard";
import { useApiError } from "../../common/errors/useApiError";
import { displayDate } from "../../common/formatting/displayDate";
import { displaySum } from "../../common/formatting/displaySum";
import { formatNumber } from "../../common/formatting/formatNumber";
import { ListLoadError } from "../../common/ListLoadError";
import { revealSection } from "../../common/scroll";
import { urls } from "../../urls";
import { calledOffPercent } from "../framework-agreement/calledOffPercent";
import { MutedCell, PanelRow, TitleCell } from "../home/panels/PanelTable";

const BAR_SCALE = 120;
const WARNING_SHARE = 80;
const LISTED_AGREEMENTS = 10;

type Opened = "expiring" | "followUp" | null;

const FRAMEWORK_LIST_PARAM = "agreements";
const OPENED_IN_ADDRESS: Record<Exclude<Opened, null>, string> = { expiring: "expiring", followUp: "follow-up" };

const useOpenedInAddress = (): [Opened, (next: Opened) => void] => {
  const [params, setParams] = useSearchParams();
  const requested = params.get(FRAMEWORK_LIST_PARAM);
  const opened =
    (Object.keys(OPENED_IN_ADDRESS) as Exclude<Opened, null>[]).find((key) => OPENED_IN_ADDRESS[key] === requested) ??
    null;
  const open = (next: Opened) =>
    setParams(
      (current) => {
        const updated = new URLSearchParams(current);
        if (next) updated.set(FRAMEWORK_LIST_PARAM, OPENED_IN_ADDRESS[next]);
        else updated.delete(FRAMEWORK_LIST_PARAM);
        return updated;
      },
      { replace: true }
    );
  return [opened, open];
};

const isInView = (element: HTMLElement) => {
  const { top, bottom } = element.getBoundingClientRect();
  return top < window.innerHeight && bottom > 0;
};

const useRevealOnArrival = (section: RefObject<HTMLElement>, isWanted: boolean, isReady: boolean) => {
  const [wantedOnArrival] = useState(isWanted);
  const settled = useRef(!wantedOnArrival);
  useEffect(() => {
    if (settled.current || !isReady) return;
    const frame = requestAnimationFrame(() => {
      settled.current = true;
      const element = section.current;
      if (element && !isInView(element)) revealSection(element);
    });
    return () => cancelAnimationFrame(frame);
  }, [section, isReady]);
};

export const FrameworkOverview = ({ projectId }: { projectId: string | null }) => {
  const { t } = useTranslation();
  const [opened, setOpened] = useOpenedInAddress();
  const section = useRef<HTMLDivElement>(null);
  const [allFollowUp, setAllFollowUp] = useState(false);
  const [allAgreements, setAllAgreements] = useState(false);
  const { currentData: data, isFetching, error, refetch } = useGetFrameworkOverviewQuery({
    projectId: projectId ?? undefined,
    allFollowUp: allFollowUp || undefined,
  });
  const { message } = useApiError();
  useRevealOnArrival(section, opened !== null, data !== undefined);

  if (!data && isFetching) return <Spinner color="smPrimary" data-testid="framework-overview-loading" />;
  if (error || !data) {
    return <ListLoadError message={message(error)} onRetry={() => void refetch()} isRetrying={isFetching} />;
  }
  const expiring = data.frameworkAgreements.filter((framework) => framework.expiresSoon);
  const shown = byNeedOfAttention(opened === "expiring" ? expiring : data.frameworkAgreements);
  const listed = allAgreements ? shown : shown.slice(0, LISTED_AGREEMENTS);
  const withoutMaximum = shown.filter((framework) => framework.maxValue == null).length;
  const toggle = (next: Exclude<Opened, null>) => setOpened(opened === next ? null : next);

  return (
    <Stack spacing="4" ref={section} data-testid="framework-overview">
      <Heading as="h3" size="sm" color="smText">
        {t("Framework agreements")}
      </Heading>
      <SimpleGrid columns={{ base: 1, sm: 3 }} spacing="4">
        <CountTile
          label={String(t("Active framework agreements"))}
          value={data.frameworkAgreements.length}
          isOpen={opened === null}
          onClick={() => setOpened(null)}
        />
        <CountTile
          label={String(t("Expiring within 180 days"))}
          value={expiring.length}
          isOpen={opened === "expiring"}
          onClick={() => toggle("expiring")}
        />
        <CountTile
          label={String(t("Call-offs after expiry or past the maximum value"))}
          value={data.followUpCount}
          isOpen={opened === "followUp"}
          onClick={() => toggle("followUp")}
          isWarning={data.followUpCount > 0}
        />
      </SimpleGrid>
      {opened === "followUp" ? (
        <FollowUpTable
          rows={data.followUp}
          total={data.followUpCount}
          onShowAll={() => setAllFollowUp(true)}
          isShowingAll={allFollowUp && isFetching}
        />
      ) : shown.length === 0 ? (
        <Text fontSize="sm" color="smMuted">
          {opened === "expiring"
            ? t("No active framework agreements expire within 180 days.")
            : t("There are no active framework agreements.")}
        </Text>
      ) : (
        <Stack spacing="2">
          {listed.map((framework) => (
            <FrameworkRow key={framework.id} framework={framework} />
          ))}
          {listed.length < shown.length && (
            <Button variant="link" size="sm" colorScheme="teal" alignSelf="flex-start" onClick={() => setAllAgreements(true)}>
              {t("Show all ({{count}})", { count: shown.length })}
            </Button>
          )}
          {withoutMaximum > 0 && (
            <Text fontSize="sm" color="smMuted">
              {t("{{count}} active framework agreements have no maximum value and show no bar.", { count: withoutMaximum })}
            </Text>
          )}
        </Stack>
      )}
    </Stack>
  );
};

const CountTile = ({
  label,
  value,
  isOpen,
  onClick,
  isWarning,
}: {
  label: string;
  value: number;
  isOpen: boolean;
  onClick: () => void;
  isWarning?: boolean;
}) => (
  <SelectableCard isSelected={isOpen} onClick={onClick}>
    <Text as="span" fontSize="sm" color="smMuted">
      {label}
    </Text>
    <Text fontSize="2xl" fontWeight="semibold" color={isWarning ? "smError" : "smText"} mt="1">
      {formatNumber(value)}
    </Text>
  </SelectableCard>
);

const usage = (framework: FrameworkUsageDto) => {
  const max = framework.maxValue == null ? undefined : Number(framework.maxValue);
  const calledOff = framework.calledOffValue == null ? undefined : Number(framework.calledOffValue);
  const hasShare = max !== undefined && max > 0 && calledOff !== undefined;
  return {
    max,
    calledOff,
    ratio: hasShare ? calledOff / max : undefined,
    percent: hasShare ? calledOffPercent(calledOff, max) : undefined,
  };
};

const attentionRank = (framework: FrameworkUsageDto): number => {
  const { max, ratio } = usage(framework);
  if (ratio !== undefined) return ratio;
  return max === undefined ? -2 : -1;
};

const byNeedOfAttention = (frameworks: FrameworkUsageDto[]): FrameworkUsageDto[] =>
  [...frameworks].sort((a, b) => attentionRank(b) - attentionRank(a));

const FrameworkRow = ({ framework }: { framework: FrameworkUsageDto }) => {
  const { t } = useTranslation();
  const { max, calledOff, percent } = usage(framework);
  const isOver = percent !== undefined && percent > 100;
  const sentence =
    percent === undefined
      ? ""
      : isOver
        ? String(t("{{percent}}% of the maximum value has been called off, more than the maximum", { percent }))
        : String(t("{{percent}}% of the maximum value has been called off", { percent }));
  const details =
    max === undefined
      ? t("{{count}} call-offs", { count: framework.callOffCount })
      : t("Called off {{calledOff}} of {{max}}, in {{count}} call-offs", {
          calledOff: displaySum({ value: calledOff ?? 0, currency: framework.currency }),
          max: displaySum({ value: max, currency: framework.currency }),
          count: framework.callOffCount,
        });

  return (
    <Box
      as={RouterLink}
      to={urls.app.frameworkAgreements.view.go(framework.id)}
      display="block"
      bg="smBackground"
      borderWidth="1px"
      borderColor="smBorder"
      borderRadius="lg"
      px="4"
      py="3"
      _hover={{ borderColor: "smPrimary" }}
      _focusVisible={{ boxShadow: "outline" }}
      data-testid={`framework-${framework.id}`}
    >
      <Flex justify="space-between" gap="3" wrap="wrap" align="baseline">
        <Text fontWeight="medium" color="smText">
          {framework.title}
        </Text>
        <Text fontSize="xs" color={framework.expiresSoon ? "smError" : "smMuted"}>
          {framework.endDate ? t("Ends {{date}}", { date: displayDate(framework.endDate) }) : t("No end date")}
        </Text>
      </Flex>
      {max === undefined ? (
        <>
          <Text fontSize="sm" color="smMuted" mt="1">
            {t("No maximum value")}
          </Text>
          <Text fontSize="xs" color="smMuted" data-testid="usage-details">
            {details}
          </Text>
        </>
      ) : percent === undefined ? (
        <Text fontSize="sm" color="smMuted" mt="1">
          {t("You do not have access to every call-off, so how much of the maximum value is called off is not shown.")}
        </Text>
      ) : (
        <>
          <UsageBar percent={percent} label={String(t("Share of the maximum value called off"))} valueText={sentence} />
          <Text fontSize="sm" color={isOver ? "smError" : "smMuted"} mt="1">
            {sentence}
          </Text>
          <Text fontSize="xs" color="smMuted" data-testid="usage-details">
            {details}
          </Text>
        </>
      )}
    </Box>
  );
};

const UsageBar = ({ percent, label, valueText }: { percent: number; label: string; valueText: string }) => {
  const { t } = useTranslation();
  return (
    <Box
      role="progressbar"
      aria-label={label}
      aria-valuenow={percent}
      aria-valuemin={0}
      aria-valuemax={Math.max(BAR_SCALE, percent)}
      aria-valuetext={valueText}
      mt="2"
    >
      <Box position="relative" aria-hidden>
        <Progress
          size="sm"
          rounded="full"
          bg="smBackgroundTertiary"
          value={(Math.min(percent, BAR_SCALE) / BAR_SCALE) * 100}
          colorScheme={percent > 100 ? "red" : percent >= WARNING_SHARE ? "orange" : "teal"}
          sx={{ "& > div": { borderRadius: "full" } }}
          data-testid="usage-fill"
        />
        {[WARNING_SHARE, 100].map((mark) => (
          <Box
            key={mark}
            position="absolute"
            top="-1"
            bottom="-1"
            left={`${(mark / BAR_SCALE) * 100}%`}
            w="2px"
            bg="smText"
            opacity={0.5}
            data-testid={`usage-mark-${mark}`}
          />
        ))}
      </Box>
      <Box position="relative" h="4" mt="1" aria-hidden>
        {[WARNING_SHARE, 100].map((mark) => (
          <Text
            key={mark}
            position="absolute"
            left={`${(mark / BAR_SCALE) * 100}%`}
            transform="translateX(-50%)"
            fontSize="2xs"
            lineHeight="4"
            color="smMuted"
            whiteSpace="nowrap"
            data-testid={`usage-mark-label-${mark}`}
          >
            {t("{{percent}}%", { percent: mark })}
          </Text>
        ))}
      </Box>
    </Box>
  );
};

const FollowUpTable = ({
  rows,
  total,
  onShowAll,
  isShowingAll,
}: {
  rows: CallOffFollowUpDto[];
  total: number;
  onShowAll: () => void;
  isShowingAll: boolean;
}) => {
  const { t } = useTranslation();
  const stacked = useBreakpointValue({ base: true, md: false }, { ssr: false }) ?? false;
  if (rows.length === 0) {
    return (
      <Text fontSize="sm" color="smMuted">
        {t("No call-offs need follow-up.")}
      </Text>
    );
  }
  const callOffs = rows.map((row) => ({
    key: row.contractId ?? row.miniCompetitionId ?? row.title,
    to: row.contractId
      ? urls.app.contracts.view.go(row.contractId)
      : urls.app.sourcingEvents.view.go(row.miniCompetitionId ?? ""),
    title: row.contractId ? row.title : `${row.title} · ${t("Awarded, no contract yet")}`,
    reason: reasonText(t, row.reasons),
    frameworkAgreement: row.frameworkAgreementTitle,
    supplier: row.supplierName ?? "—",
    value: row.value == null ? "—" : displaySum({ value: Number(row.value), currency: row.currency }),
    awarded: displayDate(row.awardedAt),
  }));
  return (
    <Stack spacing="2" data-testid="call-off-follow-up">
      <Text fontSize="sm" color="smMuted">
        {t("These call-offs need follow-up: they were made after the framework agreement ended, or took the total past its maximum value.")}
      </Text>
      {stacked ? (
        <Stack spacing="2">
          {callOffs.map((callOff) => (
            <LinkBox
              key={callOff.key}
              bg="smBackground"
              borderWidth="1px"
              borderColor="smBorder"
              borderRadius="lg"
              px="4"
              py="3"
              _hover={{ borderColor: "smPrimary" }}
              data-testid="call-off-card"
            >
              <LinkOverlay as={RouterLink} to={callOff.to} fontWeight="medium" color="smText" overflowWrap="anywhere">
                {callOff.title}
              </LinkOverlay>
              <Text fontSize="sm" color="smError">
                {callOff.reason}
              </Text>
              <Text fontSize="sm" color="smMuted" mt="1" overflowWrap="anywhere">
                {callOff.frameworkAgreement} · {callOff.supplier}
              </Text>
              <Text fontSize="sm" color="smMuted">
                {callOff.value} · {t("Awarded {{date}}", { date: callOff.awarded })}
              </Text>
            </LinkBox>
          ))}
        </Stack>
      ) : (
        <Box overflowX="auto">
          <Table variant="simple" size="sm">
            <Thead>
              <Tr>
                <Th>{t("Call-off")}</Th>
                <Th>{t("Framework agreement")}</Th>
                <Th>{t("Supplier")}</Th>
                <Th>{t("Value")}</Th>
                <Th>{t("Awarded")}</Th>
              </Tr>
            </Thead>
            <Tbody>
              {callOffs.map((callOff) => (
                <PanelRow key={callOff.key} to={callOff.to}>
                  <TitleCell title={callOff.title} subtitle={callOff.reason} subtitleColor="smError" clampLines={2} />
                  <MutedCell whiteSpace="normal">{callOff.frameworkAgreement}</MutedCell>
                  <MutedCell whiteSpace="normal">{callOff.supplier}</MutedCell>
                  <MutedCell whiteSpace="nowrap">{callOff.value}</MutedCell>
                  <MutedCell whiteSpace="nowrap">{callOff.awarded}</MutedCell>
                </PanelRow>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}
      {total > rows.length && (
        <Button variant="link" size="sm" colorScheme="teal" alignSelf="flex-start" onClick={onShowAll} isLoading={isShowingAll}>
          {t("Show all ({{count}})", { count: total })}
        </Button>
      )}
    </Stack>
  );
};

const reasonText = (t: TFunction, reasons: CallOffFollowUpReasonDto[]): string =>
  reasons
    .map((reason) =>
      reason === CallOffFollowUpReasonDto.AfterEnd
        ? String(t("Made after the agreement ended"))
        : String(t("Past the maximum value"))
    )
    .join(" · ");

